import { CircularProgress } from "@material-ui/core";
import { Document, Page, pdf } from "@react-pdf/renderer";
import React, { useEffect, useMemo, useState } from "react";
import {
    FiChevronDown,
    FiChevronUp,
    FiFileText,
    FiSearch,
} from "react-icons/fi";
import { IoWarningOutline } from "react-icons/io5";
import { MdDownload, MdFileDownloadOff, MdOutlineEdit } from "react-icons/md";
import CustomTooltip from "../../../common/Tooltip";
import CustomShowModal from "../../../components/Common/CustomShowModal.js";
import PdfFooter from "../PortInformation/pdfFooter.js";
import PdfHeader from "../PortInformation/pdfHeader.js";
import { getInvoiceById, postInvoice } from "./action.js";
import CreateInvoiceModal from "./CreateInvoiceModal.js";
import InvoicePdf from "./InvoicePdf.js";

const FinalInvoiceModel = ({
    opsId,
    finalInvoiceDetails = [],
    refetch,
    isReadOnly,
    contractType,
}) => {
    // Decouple accordion open/closed state from server data
    const [expandedParties, setExpandedParties] = useState(() => new Set());
    const [unselectedItems, setUnselectedItems] = useState(() => new Set());
    const [isPosting, setIsPosting] = useState(false);
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const [openPostConfirmModal, setOpenPostConfirmModal] = useState({
        isOpen: false,
        invoiceId: null,
    });

    const [createInvoiceModal, setCreateInvoiceModal] = useState({
        isModalOpen: false,
        selectedItems: null,
        counterPartyId: null,
        triggeredFromRow: null,
        invoiceId: null,
        partyData: null,
        isEdit: false,
    });

    // Derive groupedDetails purely from props via useMemo (avoids flickering & unmounting)
    const groupedDetails = useMemo(() => {
        const details = {};

        (finalInvoiceDetails || []).forEach((item) => {
            (item.invoiceDetails || []).forEach((detail) => {
                const {
                    counterParty,
                    counterPartyId,
                    name,
                    amount,
                    amt,
                } = detail;
                const description =
                    detail.description ||
                    detail.desc ||
                    item.description ||
                    item.desc ||
                    "";

                const numericAmt = Number(amount || amt || 0);
                if (!counterPartyId || isNaN(numericAmt) || numericAmt === 0) return;

                if (!details[counterPartyId]) {
                    details[counterPartyId] = {
                        name: name || counterParty || "Unknown Party",
                        totalAmt: 0,
                        details: [],
                    };
                }

                details[counterPartyId].totalAmt += numericAmt;
                const { invoiceDetails, ...rest } = item;
                details[counterPartyId].details.push({
                    ...rest,
                    totalAmt: numericAmt,
                    invoiceDetail: {
                        ...detail,
                        description,
                        amt: numericAmt,
                        isCargo: !!detail.cargoId,
                        cargoId: detail.cargoId || item.cargoId,
                    },
                    description,
                    isCargo: !!detail.cargoId,
                });
            });
        });

        return details;
    }, [finalInvoiceDetails]);

    // Expand the first party by default if none are currently expanded
    useEffect(() => {
        const keys = Object.keys(groupedDetails);
        if (keys.length > 0) {
            setExpandedParties((prev) => {
                if (prev.size === 0) {
                    return new Set([keys[0]]);
                }
                return prev;
            });
        }
    }, [groupedDetails]);

    const toggleExpansion = (counterPartyId) => {
        setExpandedParties((prev) => {
            const next = new Set(prev);
            if (next.has(counterPartyId)) {
                next.delete(counterPartyId);
            } else {
                next.add(counterPartyId);
            }
            return next;
        });
    };

    const getItemKey = (item, counterPartyId, index) => {
        return item.invoiceDetail?._id || item._id || `${counterPartyId}-${index}`;
    };

    const isItemSelected = (item, counterPartyId, index) => {
        if (item.invoiceDetail?.isInvoiceGenerated) return false;
        const key = getItemKey(item, counterPartyId, index);
        return !unselectedItems.has(key);
    };

    const handleCheckboxChange = (counterPartyId, index, item, checked) => {
        const key = getItemKey(item, counterPartyId, index);
        setUnselectedItems((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleSelectAll = (counterPartyId, partyDetails, checked) => {
        setUnselectedItems((prev) => {
            const next = new Set(prev);
            partyDetails.forEach((item, index) => {
                const key = getItemKey(item, counterPartyId, index);
                if (!item.invoiceDetail?.isInvoiceGenerated) {
                    if (checked) {
                        next.delete(key);
                    } else {
                        next.add(key);
                    }
                }
            });
            return next;
        });
    };

    const handleRowInvoiceClick = (counterPartyId, itemIndex) => {
        setExpandedParties((prev) => new Set([...prev, counterPartyId]));
        const partyData = groupedDetails[counterPartyId];
        if (!partyData) return;

        let selectedItems = partyData.details.filter((item, idx) =>
            isItemSelected(item, counterPartyId, idx)
        );
        if ((!selectedItems || selectedItems.length === 0) && partyData?.details?.[itemIndex]) {
            selectedItems = [partyData.details[itemIndex]];
        }

        setCreateInvoiceModal({
            counterPartyId,
            selectedItems,
            partyData,
            triggeredFromRow: itemIndex,
            isModalOpen: true,
            isEdit: false,
            invoiceId: null,
        });
    };

    const handleGenerateInvoiceForParty = (counterPartyId) => {
        setExpandedParties((prev) => new Set([...prev, counterPartyId]));
        const partyData = groupedDetails[counterPartyId];
        if (!partyData) return;

        const selectedItems = partyData.details.filter((item, idx) =>
            isItemSelected(item, counterPartyId, idx)
        );

        setCreateInvoiceModal({
            counterPartyId,
            selectedItems,
            partyData,
            triggeredFromRow: null,
            isModalOpen: true,
            isEdit: false,
            invoiceId: null,
        });
    };

    const handleEditInvoiceClick = (counterPartyId, invoiceId) => {
        setExpandedParties((prev) => new Set([...prev, counterPartyId]));
        const partyData = groupedDetails[counterPartyId];
        if (!partyData) return;

        const selectedItems = partyData.details.filter(
            (item) => item.invoiceDetail && item.invoiceDetail.invoiceId === invoiceId
        );

        setCreateInvoiceModal({
            counterPartyId,
            selectedItems,
            invoiceId,
            partyData,
            isEdit: true,
            isModalOpen: true,
        });
    };

    const handlePostInvoiceClick = (invoiceId) => {
        setOpenPostConfirmModal({
            invoiceId,
            isOpen: true,
        });
    };

    const handleConfirmPostInvoiceClick = async () => {
        if (!openPostConfirmModal?.invoiceId || isPosting) return;
        try {
            setIsPosting(true);
            const response = await getInvoiceById({
                id: openPostConfirmModal?.invoiceId,
            });
            const selectedItems = response.data?.invoiceDetail || [];

            const separated = {
                cargo: selectedItems.filter((item) => item.isCargo || item.cargoId),
                vessel: selectedItems.filter((item) => !(item.isCargo || item.cargoId)),
            };

            await postInvoice({
                finalPdfData: response.data,
                separated,
                contractType,
                id: openPostConfirmModal?.invoiceId,
            });

            setOpenPostConfirmModal({ invoiceId: null, isOpen: false });
            await refetch?.();
        } catch (error) {
            console.error("Error posting invoice:", error);
        } finally {
            setIsPosting(false);
        }
    };

    const handleDownloadInvoice = async (invoiceId) => {
        if (!invoiceId || downloadingInvoiceId) return;
        try {
            setDownloadingInvoiceId(invoiceId);
            const response = await getInvoiceById({ id: invoiceId });

            const doc = (
                <Document>
                    <Page size="A4" orientation="landscape" style={{ paddingBottom: 35 }}>
                        <PdfHeader />
                        <InvoicePdf invoiceDetails={response.data} />
                        <PdfFooter />
                    </Page>
                </Document>
            );

            const pdfBlob = await pdf(doc).toBlob();
            const url = window.URL.createObjectURL(pdfBlob);
            const element = document.createElement("a");
            element.href = url;
            element.setAttribute("download", `${response.data?._id || invoiceId}-invoice.pdf`);
            element.style.display = "none";
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading invoice:", error);
        } finally {
            setDownloadingInvoiceId(null);
        }
    };

    const handleModalClose = () => {
        setCreateInvoiceModal({
            isModalOpen: false,
            selectedItems: null,
            counterPartyId: null,
            triggeredFromRow: null,
            invoiceId: null,
            partyData: null,
            isEdit: false,
        });
    };

    const filteredCounterParties = Object.entries(groupedDetails).filter(
        ([_, partyData]) =>
            partyData.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="flex flex-col gap-1.5 text-[11px] font-medium">
            <div className="flex flex-col space-y-1.5">
                {/* Search Bar */}
                <div className="flex justify-end items-center mb-1">
                    <div className="relative w-60">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <FiSearch className="h-3 w-3 text-slate-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search counterparties..."
                            className="block w-full pl-7 pr-2.5 py-0.5 text-[11px] font-medium rounded-none border border-slate-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-amber-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Counterparties & Invoice Accordion Table */}
                <div className="overflow-hidden border border-[#cbd5e1] rounded-none bg-white scroll-thin">
                    <table className="min-w-full border-collapse text-[11px]">
                        <thead className="bg-[#e2e8f0] text-[#1e293b] h-[22px]">
                            <tr>
                                <th
                                    scope="col"
                                    className="px-2 py-0.5 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                                >
                                    Counterparty
                                </th>
                                <th
                                    scope="col"
                                    className="px-2 py-0.5 text-center font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] w-24"
                                >
                                    Items
                                </th>
                                <th
                                    scope="col"
                                    className="px-2 py-0.5 text-right font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] w-32"
                                >
                                    Total Amount
                                </th>
                                <th
                                    scope="col"
                                    className="px-2 py-0.5 text-right font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-b border-[#cbd5e1] w-40"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y-0">
                            {filteredCounterParties.length > 0 ? (
                                filteredCounterParties.map(([counterPartyId, partyData]) => {
                                    const isExpanded = expandedParties.has(counterPartyId);
                                    const availableItemsCount = partyData.details.filter(
                                        (item) => !item.invoiceDetail?.isInvoiceGenerated
                                    ).length;
                                    const selectedCount = partyData.details.filter((item, idx) =>
                                        isItemSelected(item, counterPartyId, idx)
                                    ).length;

                                    return (
                                        <React.Fragment key={counterPartyId}>
                                            {/* Counterparty Header Row */}
                                            <tr
                                                className="hover:bg-amber-50/50 transition-colors cursor-pointer h-[24px]"
                                                onClick={() => toggleExpansion(counterPartyId)}
                                            >
                                                <td className="px-2 py-0.5 whitespace-nowrap border-r border-b border-[#cbd5e1]">
                                                    <div className="flex gap-1.5 items-center">
                                                        {isExpanded ? (
                                                            <FiChevronUp className="h-3.5 w-3.5 text-slate-500" />
                                                        ) : (
                                                            <FiChevronDown className="h-3.5 w-3.5 text-slate-500" />
                                                        )}
                                                        <div className="font-semibold text-slate-800 text-[11px]">
                                                            {partyData.name}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-0.5 whitespace-nowrap text-center bg-[#f0f9ff] border-r border-b border-[#cbd5e1]">
                                                    <span className="px-1.5 py-[1px] inline-flex items-center justify-center text-[10px] font-medium text-[#1e293b]">
                                                        {partyData.details.length} items
                                                    </span>
                                                </td>
                                                <td className="px-2 py-0.5 whitespace-nowrap text-right text-slate-900 font-semibold text-[11px] border-r border-b border-[#cbd5e1] bg-[#f0f4f9]">
                                                    {partyData.totalAmt.toFixed(2)}
                                                </td>
                                                {!isReadOnly ? (
                                                    <td className="px-2 py-0.5 whitespace-nowrap text-right font-medium border-b border-[#cbd5e1]">
                                                        <div className="flex justify-end items-center space-x-2">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleGenerateInvoiceForParty(counterPartyId);
                                                                }}
                                                                disabled={selectedCount === 0}
                                                                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-none text-[11px] font-medium transition ${
                                                                    selectedCount === 0
                                                                        ? "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
                                                                        : "bg-[#313C4C] text-white hover:bg-[#0d508f] shadow-xs cursor-pointer"
                                                                }`}
                                                            >
                                                                <FiFileText className="h-3 w-3" />
                                                                <span>Generate Invoice</span>
                                                            </button>
                                                        </div>
                                                    </td>
                                                ) : (
                                                    <td className="border-b border-[#cbd5e1]"></td>
                                                )}
                                            </tr>

                                            {/* Expanded Line Items Table */}
                                            {isExpanded && (
                                                <tr className="bg-slate-50/50">
                                                    <td colSpan="4" className="px-2 py-1.5 border-b border-[#cbd5e1]">
                                                        <div className="space-y-1.5">
                                                            {/* Batch Selection Controls */}
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={
                                                                            availableItemsCount > 0 &&
                                                                            partyData.details.every(
                                                                                (item, idx) =>
                                                                                    item.invoiceDetail?.isInvoiceGenerated ||
                                                                                    isItemSelected(item, counterPartyId, idx)
                                                                            )
                                                                        }
                                                                        onChange={(e) => {
                                                                            handleSelectAll(
                                                                                counterPartyId,
                                                                                partyData.details,
                                                                                e.target.checked
                                                                            );
                                                                        }}
                                                                        disabled={availableItemsCount === 0}
                                                                        className="h-3.5 w-3.5 text-[#313C4C] rounded border-slate-300 focus:ring-[#313C4C] cursor-pointer"
                                                                    />
                                                                    <label className="ml-2 text-[11px] font-medium text-slate-700">
                                                                        Select all {availableItemsCount} ungenerated items
                                                                    </label>
                                                                </div>
                                                                <span className="text-[11px] text-slate-500 font-medium">
                                                                    Selected: {selectedCount} / {availableItemsCount}
                                                                </span>
                                                            </div>

                                                            {/* Inner Table */}
                                                            <div className="overflow-hidden border border-[#cbd5e1] rounded-none bg-white">
                                                                <table className="min-w-full border-collapse text-[11px]">
                                                                    <thead className="bg-[#e2e8f0] text-[#1e293b] h-[22px]">
                                                                        <tr>
                                                                            <th
                                                                                scope="col"
                                                                                className="px-1 py-0.5 text-center font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] w-7"
                                                                            >
                                                                                #
                                                                            </th>
                                                                            <th
                                                                                scope="col"
                                                                                className="px-1.5 py-0.5 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                                                                            >
                                                                                Item
                                                                            </th>
                                                                            <th
                                                                                scope="col"
                                                                                className="px-2 py-0.5 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                                                                            >
                                                                                Details
                                                                            </th>
                                                                            <th
                                                                                scope="col"
                                                                                className="px-2 py-0.5 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                                                                            >
                                                                                Description
                                                                            </th>
                                                                            <th
                                                                                scope="col"
                                                                                className="px-2 py-0.5 text-right font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] w-28"
                                                                            >
                                                                                Amount
                                                                            </th>
                                                                            <th
                                                                                scope="col"
                                                                                className="px-2 py-0.5 text-center font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-b border-[#cbd5e1] w-32"
                                                                            >
                                                                                Action
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y-0">
                                                                        {partyData.details.map((detail, index) => {
                                                                            const isPosted = detail.invoiceDetail?.isPosted;
                                                                            const isInvoiceGenerated = detail.invoiceDetail?.isInvoiceGenerated;
                                                                            const itemDescription =
                                                                                detail.invoiceDetail?.description || detail.description;
                                                                            const checked = isInvoiceGenerated
                                                                                ? true
                                                                                : isItemSelected(detail, counterPartyId, index);

                                                                            return (
                                                                                <tr
                                                                                    key={index}
                                                                                    className={`hover:bg-amber-50/50 transition-colors h-[22px] ${
                                                                                        isInvoiceGenerated ? "bg-slate-50/60" : ""
                                                                                    }`}
                                                                                >
                                                                                    <td className="px-1 py-0.5 text-center whitespace-nowrap text-[11px] text-slate-500 font-medium border-r border-b border-[#cbd5e1] bg-[#f0f4f9] w-7 align-middle">
                                                                                        {index + 1}
                                                                                    </td>
                                                                                    <td className="px-1.5 py-0.5 whitespace-nowrap border-r border-b border-[#cbd5e1] align-middle">
                                                                                        <div className="flex items-center justify-between w-full">
                                                                                            <div className="flex items-center min-w-0 pr-2">
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={checked}
                                                                                                    onChange={(e) => {
                                                                                                        handleCheckboxChange(
                                                                                                            counterPartyId,
                                                                                                            index,
                                                                                                            detail,
                                                                                                            e.target.checked
                                                                                                        );
                                                                                                    }}
                                                                                                    disabled={isInvoiceGenerated}
                                                                                                    className={`h-3.5 w-3.5 rounded border-slate-300 focus:ring-[#313C4C] cursor-pointer ${
                                                                                                        isInvoiceGenerated
                                                                                                            ? "text-slate-400 cursor-not-allowed"
                                                                                                            : "text-[#313C4C]"
                                                                                                    }`}
                                                                                                />
                                                                                                <span
                                                                                                    className={`ml-2 font-medium text-[11px] truncate ${
                                                                                                        isInvoiceGenerated
                                                                                                            ? "text-slate-500"
                                                                                                            : "text-slate-800"
                                                                                                    }`}
                                                                                                >
                                                                                                    {detail.invoiceDetail?.label || detail.label}
                                                                                                </span>
                                                                                            </div>
                                                                                            {isInvoiceGenerated && (
                                                                                                <span className="ml-auto flex-shrink-0 inline-flex items-center px-1.5 py-[1px] rounded-none text-[10px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-300">
                                                                                                    Invoice Generated
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-2 py-0.5 border-r border-b border-[#cbd5e1] align-middle">
                                                                                        <div className="flex flex-wrap items-center gap-1.5">
                                                                                            {detail.port && (
                                                                                                <span className="inline-flex items-center px-1.5 py-[1px] rounded-none text-[10px] font-medium bg-sky-50 text-[#1e293b] border border-sky-300">
                                                                                                    Port: {detail.port}
                                                                                                </span>
                                                                                            )}
                                                                                            {detail.operation && (
                                                                                                <span className="inline-flex items-center px-1.5 py-[1px] rounded-none text-[10px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-300">
                                                                                                    {detail.operation}
                                                                                                </span>
                                                                                            )}
                                                                                            {detail.vesselName && (
                                                                                                <span className="inline-flex items-center px-1.5 py-[1px] rounded-none text-[10px] font-medium bg-rose-50 text-rose-800 border border-rose-300">
                                                                                                    {detail.vesselName}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-2 py-0.5 text-[11px] font-medium text-slate-700 max-w-[200px] whitespace-nowrap border-r border-b border-[#cbd5e1] align-middle">
                                                                                        {itemDescription ? (
                                                                                            <CustomTooltip
                                                                                                text={itemDescription}
                                                                                                position="top"
                                                                                            >
                                                                                                <span className="font-medium text-slate-800 text-[11px] truncate block max-w-[180px] cursor-help">
                                                                                                    {itemDescription}
                                                                                                </span>
                                                                                            </CustomTooltip>
                                                                                        ) : (
                                                                                            <span className="text-slate-400 italic text-[11px] font-medium">
                                                                                                —
                                                                                            </span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="px-2 py-0.5 whitespace-nowrap text-right text-slate-900 font-semibold text-[11px] border-r border-b border-[#cbd5e1] bg-[#f0f4f9] align-middle">
                                                                                        {(typeof detail.invoiceDetail?.amt === "string"
                                                                                            ? detail.invoiceDetail.amt
                                                                                            : detail.invoiceDetail?.amt?.toFixed(2)) || 0}
                                                                                    </td>
                                                                                    <td className="px-2 py-0.5 whitespace-nowrap text-center border-b border-[#cbd5e1] align-middle">
                                                                                        {isInvoiceGenerated ? (
                                                                                            <div className="flex items-center justify-center gap-1.5">
                                                                                                <button
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleDownloadInvoice(
                                                                                                            detail.invoiceDetail?.invoiceId
                                                                                                        );
                                                                                                    }}
                                                                                                    disabled={downloadingInvoiceId === detail.invoiceDetail?.invoiceId}
                                                                                                    className="p-1 rounded text-[#313C4C] hover:bg-sky-50 transition cursor-pointer flex items-center justify-center disabled:opacity-50"
                                                                                                    title="Download invoice"
                                                                                                >
                                                                                                    {downloadingInvoiceId === detail.invoiceDetail?.invoiceId ? (
                                                                                                        <CircularProgress size={12} className="!text-[#313C4C]" />
                                                                                                    ) : (
                                                                                                        <MdDownload className="h-3.5 w-3.5" />
                                                                                                    )}
                                                                                                </button>
                                                                                                {!isReadOnly && (
                                                                                                    <div className="flex items-center gap-1">
                                                                                                        <button
                                                                                                            className={`p-1 rounded transition flex items-center justify-center ${
                                                                                                                isPosted
                                                                                                                    ? "text-slate-400 cursor-not-allowed"
                                                                                                                    : "text-[#313C4C] hover:bg-sky-50 cursor-pointer"
                                                                                                            }`}
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                if (!isPosted) {
                                                                                                                    handleEditInvoiceClick(
                                                                                                                        counterPartyId,
                                                                                                                        detail.invoiceDetail?.invoiceId
                                                                                                                    );
                                                                                                                }
                                                                                                            }}
                                                                                                            disabled={isPosted}
                                                                                                            title={isPosted ? "Invoice posted" : "Edit invoice"}
                                                                                                        >
                                                                                                            <MdOutlineEdit className="h-3.5 w-3.5" />
                                                                                                        </button>
                                                                                                        <button
                                                                                                            className={`px-2 py-[2px] rounded-none text-[10px] font-medium leading-none flex items-center justify-center transition ${
                                                                                                                isPosted
                                                                                                                    ? "text-slate-400 cursor-not-allowed bg-slate-100"
                                                                                                                    : "text-[#313C4C] bg-sky-50 hover:bg-sky-100 cursor-pointer border border-sky-200"
                                                                                                            }`}
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                if (!isPosted) {
                                                                                                                    handlePostInvoiceClick(
                                                                                                                        detail.invoiceDetail?.invoiceId
                                                                                                                    );
                                                                                                                }
                                                                                                            }}
                                                                                                            disabled={isPosted}
                                                                                                        >
                                                                                                            {isPosted ? "Posted" : "Post"}
                                                                                                        </button>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        ) : (
                                                                                            !isReadOnly && (
                                                                                                <button
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleRowInvoiceClick(
                                                                                                            counterPartyId,
                                                                                                            index
                                                                                                        );
                                                                                                    }}
                                                                                                    disabled={!checked}
                                                                                                    className={`p-1 rounded-none transition inline-flex items-center justify-center ${
                                                                                                        checked
                                                                                                            ? "text-[#313C4C] hover:bg-sky-50 cursor-pointer"
                                                                                                            : "text-slate-300 cursor-not-allowed"
                                                                                                    }`}
                                                                                                    title={
                                                                                                        checked
                                                                                                            ? "Create invoice for selected items"
                                                                                                            : "Select item to enable"
                                                                                                    }
                                                                                                >
                                                                                                    {checked ? (
                                                                                                        <MdDownload className="h-3.5 w-3.5" />
                                                                                                    ) : (
                                                                                                        <MdFileDownloadOff className="h-3.5 w-3.5" />
                                                                                                    )}
                                                                                                </button>
                                                                                            )
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center">
                                        <FiFileText className="mx-auto h-8 w-8 text-slate-300" />
                                        <h3 className="mt-2 text-[11px] font-medium text-slate-500">
                                            No counterparty found
                                        </h3>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create / Edit Invoice Modal */}
            {createInvoiceModal.isModalOpen && (
                <CustomShowModal
                    open={createInvoiceModal.isModalOpen}
                    handleClose={handleModalClose}
                    title={createInvoiceModal.isEdit ? "Edit Invoice" : "Create Invoice"}
                    maxWidth="md"
                >
                    <CreateInvoiceModal
                        isModalOpen={createInvoiceModal.isModalOpen}
                        invoiceDetail={createInvoiceModal.selectedItems}
                        selectedItems={createInvoiceModal.selectedItems}
                        counterPartyId={createInvoiceModal.counterPartyId}
                        triggeredFromRow={createInvoiceModal.triggeredFromRow}
                        invoiceId={createInvoiceModal.invoiceId}
                        partyData={createInvoiceModal.partyData}
                        isEdit={createInvoiceModal.isEdit}
                        handleModalClose={handleModalClose}
                        onClose={handleModalClose}
                        opsId={opsId}
                        refetch={refetch}
                        isReadOnly={isReadOnly}
                        contractType={contractType}
                    />
                </CustomShowModal>
            )}

            {/* Post Confirmation Modal */}
            {openPostConfirmModal?.isOpen && (
                <CustomShowModal
                    open={openPostConfirmModal?.isOpen}
                    handleClose={() =>
                        setOpenPostConfirmModal({ isOpen: false, invoiceId: null })
                    }
                    title="Confirm Post Invoice"
                    maxWidth="sm"
                >
                    <div className="p-3 text-[11px] font-medium">
                        <div className="flex items-center gap-2.5 mb-3 text-amber-700 bg-amber-50 p-2 border border-amber-200">
                            <IoWarningOutline className="w-5 h-5 flex-shrink-0" />
                            <span className="text-[11px] leading-snug">
                                Are you sure you want to post this invoice? Once posted, this invoice will be locked and cannot be edited.
                            </span>
                        </div>
                        <div className="flex justify-end gap-2 mt-3 pt-2 border-t border-slate-200">
                            <button
                                type="button"
                                onClick={() =>
                                    setOpenPostConfirmModal({ isOpen: false, invoiceId: null })
                                }
                                disabled={isPosting}
                                className="px-3 py-1 text-[11px] font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-none transition cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmPostInvoiceClick}
                                disabled={isPosting}
                                className="px-3 py-1 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#0d508f] rounded-none transition shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                            >
                                {isPosting && <CircularProgress size={11} className="!text-white" />}
                                <span>Confirm Post</span>
                            </button>
                        </div>
                    </div>
                </CustomShowModal>
            )}
        </div>
    );
};

export default FinalInvoiceModel;
